import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import StatusChip from '../ui/StatusChip';
import {
    activeThreadIdFromLocation,
    CONVERSATION_ENGAGED_EVENT,
    collapseInboxRows,
    fetchAlertBrands,
    fetchNotificationInbox,
    markAllNotificationsRead,
    markNotificationRead,
    notificationDeliveryLabel,
    openInApp,
    shouldShowPopup,
} from '../../lib/notificationInbox';
import NotificationKindIcon from './NotificationKindIcon';
import { announceOpenConversation, sessionIdFromUrl } from '../../lib/conversationLinks';
import NotificationPopupStack from './NotificationPopupStack';
import { notificationItemView, notificationGroupView, isCriticalAlert, isUrgentAlert } from './notificationItemView';
import {
    cardLatest, emptyPopupStack, hiddenCardCount, popupStackReducer, popupThreadKey, visibleCards,
} from '../../lib/notificationPopupStack';
import {
    SOUND_DEFAULTS, SOUND_SETTINGS_CHANGED_EVENT, decideAlertSound, hasUserActivated, normalizeSoundSettings, playTone, preloadTones, trackUserActivation,
} from '../../lib/alertSounds';
import { fetchMySoundSettings } from '../../lib/soundSettingsApi';
import { supabaseClient } from '../../config/supabase';
import { liveAlertsMonitor } from '../../lib/liveAlerts';
import { checkWorkerUpdate } from '../../lib/pushWorkerVersion';


export const BELL_POLL_MS = 30000;
// BL-177 Step 2f/2g: new alerts arrive by Supabase Realtime (instant), and by the push worker's message. The timed check is only
// the backup: every 30 s until a live path has actually DELIVERED an alert (a channel that says SUBSCRIBED proves nothing), then
// a slow safety net (2 min) for as long as the live paths keep delivering.
export const BELL_BACKUP_POLL_MS = 120000;

// Monochrome bell (inherits the button's text colour) in place of the yellow emoji.
function BellIcon() {
    return (
        <svg
            aria-hidden="true"
            data-testid="bell-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
    );
}
export const MY_NOTIFICATIONS_PATH = '/my-notifications';

// BL-177 Step 2d (board 02): the bell panel + the in-app pop-up.
//  - Row: teal dot when unread, channel icon (no colour), title "Contact · Company", preview, time (dim), colour flag
//    chip when relevant. Clicking a row opens the conversation AND marks it read.
//  - "Mark all read" and "Settings" (3D buttons).
//  - A new alert pops up bottom-right for 8 s unless the person is already reading that conversation; then it lives
//    here. New alerts arrive by the push worker's message (instant when this device has push) or a 30 s check.
function NotificationCenterBell({
    userId,
    orgId,
    theme = 'light',
    pollMs = BELL_POLL_MS,
    getActiveThreadId = activeThreadIdFromLocation,
    navigateTo = openInApp,
}) {
    const dark = theme === 'dark';
    const panelRef = useRef(null);
    const bellButtonRef = useRef(null);
    const dialogRef = useRef(null);
    const seenIdsRef = useRef(null);
    const pendingConversationReadsRef = useRef(new Set());
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open) return undefined;
        const trigger = bellButtonRef.current;
        dialogRef.current?.querySelector('button, a')?.focus();
        return () => { trigger?.focus(); };
    }, [open]);

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // BL-177 Step 2o: every alert that pops up is kept on a stack (newest on top), not one overwritten slot.
    const [popups, dispatchPopups] = useReducer(popupStackReducer, undefined, emptyPopupStack);
    const [brands, setBrands] = useState(null);
    const soundRef = useRef(normalizeSoundSettings(SOUND_DEFAULTS));
    const poppedKeysRef = useRef(new Set());
    const loadChainRef = useRef(Promise.resolve());
    // True only after a live path (Realtime row change or the database's Broadcast nudge) has delivered an alert to this page.
    const [liveProven, setLiveProven] = useState(false);

    // Alert sounds (BL-177 Step 2e): the person's own settings, read once per sign-in and again after they change them.
    // Browsers only allow sound once the page has had a click, so listen for the first one.
    useEffect(() => {
        trackUserActivation();
        let cancelled = false;
        if (!userId || !orgId) return undefined;
        const load = () => Promise.resolve(fetchMySoundSettings(supabaseClient)).then((result) => { if (!cancelled && result?.settings) { soundRef.current = result.settings; preloadTones([result.settings.standardTone, result.settings.urgentTone]); } }).catch(() => {});
        load();
        window.addEventListener(SOUND_SETTINGS_CHANGED_EVENT, load);
        return () => { cancelled = true; window.removeEventListener(SOUND_SETTINGS_CHANGED_EVENT, load); };
    }, [orgId, userId]);

    // One sound for a batch of new alerts: the strongest one decides (an urgent alert beats a standard one).
    const playAlertSound = useCallback((candidates) => {
        const urgent = candidates.find((item) => isCriticalAlert(item)) || candidates.find((item) => isUrgentAlert(item));
        const lead = urgent || candidates[0];
        if (!lead) return;
        const decision = decideAlertSound({
            settings: soundRef.current,
            alert: {
                urgent: Boolean(urgent), critical: candidates.some((item) => isCriticalAlert(item)),
                sessionId: lead.payload?.sessionId || '',
            },
            now: new Date(),
            activeThreadId: getActiveThreadId(),
            userActivated: hasUserActivated(),
        });
        if (decision.play) playTone(decision.tone, decision.volume);
    }, [getActiveThreadId]);

    // One load at a time. A Realtime event, the push worker's message and the backup check can arrive together; running them
    // side by side would let each one see the same alert as new and pop it (and play its tone) twice.
    const runLoad = useCallback(async ({ background = false, source = 'check' } = {}) => {
        if (!userId || !orgId) {
            setNotifications([]);
            return { rows: [], popped: [] };
        }
        if (!background) setLoading(true);
        setError('');
        let result = { rows: null, popped: [] };
        try {
            const rows = await fetchNotificationInbox();
            setNotifications(rows);
            const seen = seenIdsRef.current;
            if (seen) {
                // Only alerts that arrived since the last look can pop up; the first load never does.
                const fresh = collapseInboxRows(rows).filter((item) => !item.isRead && item.ids.every((id) => !seen.has(id)));
                const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
                const eligible = fresh.filter((item) => shouldShowPopup(getActiveThreadId(), item));
                if (eligible.length && !hidden) {
                    dispatchPopups({ type: 'add', items: eligible });
                    eligible.forEach((item) => poppedKeysRef.current.add(item.key));
                    playAlertSound(eligible);
                    result.popped = eligible;
                    // Which path brought it, and how long after it was written: the Settings status line and the console show this.
                    const ages = eligible.map((item) => (Date.now() - Date.parse(item.createdAt || '')) / 1000).filter(Number.isFinite);
                    liveAlertsMonitor.recordArrival(source, ages.length ? Math.max(...ages) : null);
                    setLiveProven(liveAlertsMonitor.proven());
                }
            }
            seenIdsRef.current = new Set(rows.map((row) => row.id));
            result.rows = rows;
        } catch (loadError) {
            if (!background) setError(loadError.message || 'Could not load notifications.');
        } finally {
            if (!background) setLoading(false);
        }
        return result;
    }, [orgId, userId, getActiveThreadId, playAlertSound]);

    const loadNotifications = useCallback((options = {}) => {
        const next = loadChainRef.current.catch(() => {}).then(() => runLoad(options));
        loadChainRef.current = next;
        return next;
    }, [runLoad]);

    useEffect(() => {
        seenIdsRef.current = null;
        loadNotifications();
    }, [loadNotifications]);

    // Company names for alert titles (Brands & Locations), read once per signed-in org.
    useEffect(() => {
        let cancelled = false;
        if (!userId || !orgId) return undefined;
        fetchAlertBrands().then((result) => { if (!cancelled) setBrands(result); });
        return () => { cancelled = true; };
    }, [orgId, userId]);

    // Instant delivery (BL-177 Step 2f/2g). Two live paths, each on its own channel so a failure in one shows up on its own:
    //   changes   = Realtime row changes for this person's rows in public.notifications (checked against row-level security), and
    //   broadcast = the database's private "an alert arrived" nudge on ia-alerts:<user id> (migration bl177_step2g), which does not
    //               depend on that row-level-security check inside Realtime.
    // Either one loads the alert through the normal recipient-scoped inbox call, so what a person sees is unchanged.
    useEffect(() => {
        if (!userId || !orgId) return undefined;
        liveAlertsMonitor.reset();
        setLiveProven(false);
        if (typeof supabaseClient?.channel !== 'function') {
            liveAlertsMonitor.setPathStatus('changes', 'unavailable');
            liveAlertsMonitor.setPathStatus('broadcast', 'unavailable');
            return undefined;
        }
        let cancelled = false;
        const channels = [];
        let authListener = null;
        const setSocketToken = (token) => { if (token) try { supabaseClient.realtime?.setAuth?.(token); } catch { /* the client keeps its own token */ } };
        const watch = (path) => (status, failure) => {
            if (cancelled) return;
            liveAlertsMonitor.setPathStatus(path, status, failure);
            setLiveProven(liveAlertsMonitor.proven());
            // After a (re)connect, catch up on anything that arrived while it was down.
            if (status === 'SUBSCRIBED') loadNotifications({ background: true, source: 'start' });
        };
        const live = (path, id) => {
            if (cancelled) return;
            liveAlertsMonitor.recordLive(path, id);
            setLiveProven(liveAlertsMonitor.proven());
            loadNotifications({ background: true, source: path });
        };
        function open() {
            if (cancelled) return;
            liveAlertsMonitor.setPathStatus('changes', 'connecting');
            liveAlertsMonitor.setPathStatus('broadcast', 'connecting');
            try {
                channels.push(supabaseClient
                    .channel(`ia-bell-${userId}`)
                    .on('postgres_changes', {
                        event: 'INSERT', schema: 'public', table: 'notifications', filter: `recipient_user_id=eq.${userId}`,
                    }, (change) => {
                        const row = change?.new || {};
                        if (row.org_id && row.org_id !== orgId) return;
                        if (row.channel && row.channel !== 'in_app') return; // the in-app row speaks for the alert
                        live('changes', row.id);
                    })
                    .subscribe(watch('changes')));
            } catch (failure) {
                liveAlertsMonitor.setPathStatus('changes', 'CHANNEL_ERROR', failure);
            }
            try {
                channels.push(supabaseClient
                    .channel(`ia-alerts:${userId}`, { config: { private: true, broadcast: { self: false } } })
                    .on('broadcast', { event: 'alert' }, (message) => live('broadcast', message?.payload?.id))
                    .subscribe(watch('broadcast')));
            } catch (failure) {
                liveAlertsMonitor.setPathStatus('broadcast', 'CHANNEL_ERROR', failure);
            }
        }
        // The Realtime socket must carry THIS person's token (not the public key), or row-level security hides every row from it.
        // Set it before joining, and again whenever the session's token is refreshed.
        try {
            const pending = typeof supabaseClient.auth?.getSession === 'function' ? supabaseClient.auth.getSession() : null;
            const listener = supabaseClient.auth?.onAuthStateChange?.((_event, session) => setSocketToken(session?.access_token));
            authListener = listener?.data?.subscription || null;
            if (pending && typeof pending.then === 'function') {
                pending.then((result) => setSocketToken(result?.data?.session?.access_token)).catch(() => {}).then(open);
            } else {
                open();
            }
        } catch {
            open();
        }
        return () => {
            cancelled = true;
            try { authListener?.unsubscribe?.(); } catch { /* already gone */ }
            channels.forEach((channel) => { try { supabaseClient.removeChannel(channel); } catch { /* already closed */ } });
        };
    }, [loadNotifications, orgId, userId]);

    // The timed check is only the backup: every 30 s until a live path has delivered, then a slow safety net.
    useEffect(() => {
        const interval = liveProven ? Math.max(pollMs, BELL_BACKUP_POLL_MS) : pollMs;
        if (!userId || !orgId || !pollMs) return undefined;
        const timer = setInterval(() => {
            if (typeof document === 'undefined' || document.visibilityState !== 'hidden') loadNotifications({ background: true, source: 'check' });
        }, interval);
        return () => clearInterval(timer);
    }, [loadNotifications, orgId, pollMs, liveProven, userId]);

    // The push worker tells an open tab about a push (refresh now) and asks it to open a clicked notification in-app.
    useEffect(() => {
        const container = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
        if (!container?.addEventListener) return undefined;
        // BL-177 Step 2i: an app that stays open for days must still pick up a new push worker: look for one whenever the app opens.
        checkWorkerUpdate();
        // The worker asks (ia-push-handle) whether this page will show the alert itself; if so it skips the system banner.
        // Yes means: the in-app pop-up is up for it (now or a moment ago), or the person is reading that conversation.
        async function handlePushHandle(message, port) {
            let handled = false;
            try {
                const sessionId = String(message.sessionId || '').toLowerCase();
                const reading = Boolean(sessionId) && getActiveThreadId().toLowerCase() === sessionId;
                const { rows, popped } = await loadNotifications({ background: true, source: 'push' });
                const items = collapseInboxRows(rows || []);
                const mine = items.find((item) => !item.isRead && String(item.payload?.sessionId || '').toLowerCase() === sessionId);
                handled = reading || popped.length > 0 || Boolean(mine && poppedKeysRef.current.has(mine.key));
            } catch { /* the banner will show */ }
            port?.postMessage({ handled });
        }
        function handleMessage(event) {
            const message = event.data || {};
            if (message.type === 'ia-push') loadNotifications({ background: true, source: 'push' });
            if (message.type === 'ia-push-handle') handlePushHandle(message, event.ports?.[0]);
            if (message.type === 'ia-open' && typeof message.url === 'string') {
                navigateTo(message.url);
                // a push banner's click on an app that is already open: open and select that conversation too
                const pushSessionId = sessionIdFromUrl(message.url);
                if (pushSessionId) announceOpenConversation(pushSessionId, { reply: Boolean(message.reply) });
                if (message.reply && typeof window !== 'undefined') {
                    const sessionId = /session=([0-9a-f-]{36})/i.exec(message.url)?.[1] || null;
                    window.dispatchEvent(new CustomEvent('ia:reply', { detail: { threadUrl: message.url, sessionId } }));
                }
                event.ports?.[0]?.postMessage({ ok: true });
            }
        }
        container.addEventListener('message', handleMessage);
        return () => container.removeEventListener('message', handleMessage);
    }, [loadNotifications, navigateTo, getActiveThreadId]);

    useEffect(() => {
        if (!open) return undefined;
        function handleDocumentMouseDown(event) {
            if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
        }
        function handleKey(event) {
            if (event.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', handleDocumentMouseDown);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    const items = useMemo(() => collapseInboxRows(notifications), [notifications]);
    const unreadCount = items.filter((item) => !item.isRead).length;
    const activeThreadId = String(getActiveThreadId() || '').toLowerCase();

    // An alert read somewhere else (the bell, the conversation itself, another tab) leaves the pop-up stack.
    useEffect(() => {
        dispatchPopups({ type: 'prune', unreadKeys: new Set(items.filter((item) => !item.isRead).map((item) => item.key)) });
    }, [items]);

    function markLocallyRead(ids) {
        const readAt = new Date().toISOString();
        setNotifications((current) => current.map((row) => (ids.includes(row.id)
            ? { ...row, isRead: true, inboxReadAt: row.inboxReadAt || readAt }
            : row)));
    }

    useEffect(() => {
        if (!userId || !orgId || typeof window === 'undefined') return undefined;
        let active = true;
        const onConversationEngaged = (event) => {
            const sessionId = typeof event?.detail?.sessionId === 'string'
                ? event.detail.sessionId.trim().toLowerCase()
                : '';
            if (!sessionId || pendingConversationReadsRef.current.has(sessionId)) return;
            pendingConversationReadsRef.current.add(sessionId);
            (async () => {
                try {
                    const rows = await fetchNotificationInbox();
                    const unreadIds = rows
                        .filter((row) => !row.isRead && String(row.payload?.sessionId || '').toLowerCase() === sessionId)
                        .map((row) => row.id);
                    if (unreadIds.length) {
                        markLocallyRead(unreadIds);
                        const results = await Promise.allSettled(
                            unreadIds.map((notificationId) => markNotificationRead({ notificationId }))
                        );
                        const failure = results.find((result) => result.status === 'rejected');
                        if (failure) {
                            throw failure.reason || new Error('Could not mark conversation notifications read.');
                        }
                    }
                    if (active) await loadNotifications({ background: true });
                } catch (markError) {
                    if (active) {
                        setError(markError.message || 'Could not mark conversation notifications read.');
                        await loadNotifications({ background: true });
                    }
                } finally {
                    pendingConversationReadsRef.current.delete(sessionId);
                }
            })();
        };
        window.addEventListener(CONVERSATION_ENGAGED_EVENT, onConversationEngaged);
        return () => {
            active = false;
            window.removeEventListener(CONVERSATION_ENGAGED_EVENT, onConversationEngaged);
        };
    }, [loadNotifications, orgId, userId]);

    useEffect(() => {
        if (!activeThreadId) return;
        const unreadIds = notifications
            .filter((row) => !row.isRead && String(row.payload?.sessionId || '').toLowerCase() === activeThreadId)
            .map((row) => row.id);
        if (!unreadIds.length) return;
        markLocallyRead(unreadIds);
        Promise.all(unreadIds.map((notificationId) => markNotificationRead({ notificationId })))
            .catch((markError) => setError(markError.message || 'Could not mark conversation notifications read.'));
    }, [activeThreadId, notifications]);

    async function markItemRead(item) {
        const unread = item.ids.filter((id) => notifications.some((row) => row.id === id && !row.isRead));
        if (!unread.length) return;
        markLocallyRead(unread);
        try {
            await Promise.all(unread.map((notificationId) => markNotificationRead({ notificationId })));
        } catch (markError) {
            setError(markError.message || 'Could not mark that notification read.');
        }
    }

    async function openItem(item, { reply = false, alsoRead = [] } = {}) {
        const view = notificationItemView(item, new Date(), brands);
        setOpen(false);
        dispatchPopups({ type: 'dismiss', id: popupThreadKey(item) });
        if (view.url) {
            navigateTo(view.url);
            // Comms final pass (item 5): View always opens and selects the conversation, even when Communications is already on
            // screen (a URL change alone was never read after the screen mounted).
            const viewSessionId = sessionIdFromUrl(view.url) || (item.payload?.sessionId ? String(item.payload.sessionId) : '');
            if (viewSessionId) announceOpenConversation(viewSessionId, { reply });
            if (reply && typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('ia:reply', { detail: { threadUrl: view.url, sessionId: item.payload?.sessionId || null } }));
            }
        }
        await markItemRead(item);
        // A pop-up card that held several messages from this conversation: opening it reads them all.
        for (const other of alsoRead) await markItemRead(other);
    }

    // Open / Reply on a stacked pop-up card: act on its newest alert, and read every alert the card held.
    function openCard(id, { reply = false } = {}) {
        const card = popups.cards.find((entry) => entry.id === id);
        if (!card) return;
        const latest = cardLatest(card);
        return openItem(latest, { reply, alsoRead: card.items.filter((entry) => entry !== latest) });
    }

    function openPopupMessage(id, itemKey) {
        const card = popups.cards.find((entry) => entry.id === id);
        const item = card?.items.find((entry) => entry.key === itemKey);
        if (item) return openItem(item);
        return undefined;
    }

    async function handleMarkAllRead() {
        markLocallyRead(notifications.map((row) => row.id));
        try {
            await markAllNotificationsRead();
        } catch (markError) {
            setError(markError.message || 'Could not mark notifications read.');
        }
    }

    function handleSettings() {
        setOpen(false);
        navigateTo(MY_NOTIFICATIONS_PATH);
    }

    const dismissCard = useCallback((id) => dispatchPopups({ type: 'dismiss', id }), []);
    const buttonCls = dark
        ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600'
        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50';
    const popupEntries = visibleCards(popups).map((card) => ({
        id: card.id,
        version: card.version,
        view: {
            ...notificationGroupView(card.items, new Date(), brands),
            messages: [...card.items].reverse().map((item) => ({ key: item.key, ...notificationItemView(item, new Date(), brands) })),
        },
    }));

    return (
        <div className="relative ia-notification-center" ref={panelRef}>
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
                ref={bellButtonRef}
                aria-controls="staff-notification-center"
                aria-haspopup="dialog"
                aria-expanded={open}
                className={`relative inline-flex items-center justify-center rounded-full border min-w-[44px] min-h-[44px] px-3 py-1.5 leading-none ${buttonCls}`}
            >
                <BellIcon />
                {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 min-w-[1.25rem] rounded-full bg-[#B91C1C] px-1 text-center text-xs font-semibold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>
            {open && (
                <section
                    className="ia-d2 absolute right-0 z-30 mt-2 w-[26rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-[#26365A] shadow-2xl"
                    style={{ background: dark ? '#111B30' : '#FFFFFF', '--ia-ink': dark ? '#FFFFFF' : '#111827', '--ia-dim': dark ? '#CBD5E1' : '#475569' }}
                    id="staff-notification-center"
                    ref={dialogRef}
                    role="dialog"
                    aria-label="Notification center"
                >
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                        <h2 className="text-lg font-bold text-white">Notifications</h2><Button3D variant="pill" aria-label="Close notifications" onClick={() => setOpen(false)}>Close</Button3D>
                        <div className="flex items-center gap-2">
                            <Button3D variant="pill" onClick={handleMarkAllRead} disabled={unreadCount === 0}>Mark all read</Button3D>
                            <Button3D variant="pill" onClick={handleSettings}>Settings</Button3D>
                        </div>
                    </div>

                    {error && <div role="alert" className="mx-4 mb-3 rounded-lg bg-[#B91C1C] px-3 py-2 text-sm text-white">{error}</div>}
                    {loading && <div className="px-4 py-6 text-center text-sm text-white">Loading notifications…</div>}
                    {!loading && items.length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-white">No notifications yet.</div>
                    )}
                    {!loading && items.length > 0 && (
                        <ul className="max-h-[28rem] overflow-y-auto" aria-label="Notifications">
                            {items.map((item) => {
                                const view = notificationItemView(item, new Date(), brands);
                                const failedDelivery = !item.hasInApp && item.deliveryStatus === 'failed';
                                return (
                                    <li key={item.key} className="border-t border-[#1E2A42]">
                                        <button
                                            type="button"
                                            onClick={() => openItem(item)}
                                            data-unread={item.isRead ? 'false' : 'true'}
                                            aria-label={`${item.isRead ? '' : 'Unread: '}${view.title}${view.preview ? `. ${view.preview}` : ''}`}
                                            className={`flex min-h-[44px] w-full items-start gap-3 px-4 py-3 text-left hover:bg-white/5 ${item.isRead ? '' : 'bg-[#16223B]'}`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                data-testid="unread-dot"
                                                style={{backgroundColor:item.isRead?'transparent':dark?'#2DD4BF':'#0F766E'}}
                                                className={`mt-3 h-2 w-2 flex-none rounded-full ${item.isRead ? 'bg-transparent' : 'bg-[#2DD4BF]'}`}
                                            />
                                            <NotificationKindIcon kind={view.kind} />
                                            <span className="min-w-0 flex-1">
                                                <span className="flex items-start justify-between gap-2">
                                                    <span className="min-w-0 break-words text-base font-bold text-white">{view.title}</span>
                                                    <span className="ia-d2-dim flex-none whitespace-nowrap text-xs">{view.time}</span>
                                                </span>
                                                {view.preview && <span className="mt-0.5 block break-words text-sm text-white">{view.preview}</span>}
                                                {view.chip && <span className="mt-1 block"><StatusChip tone={view.chip.tone}>{view.chip.label}</StatusChip></span>}
                                                {failedDelivery && (
                                                    <span className="mt-1 block text-xs text-white">{item.channel} · {notificationDeliveryLabel(item)}</span>
                                                )}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            )}
            <NotificationPopupStack
                entries={popupEntries}
                hiddenCount={hiddenCardCount(popups)}
                onOpen={(id) => openCard(id)}
                onReply={(id) => openCard(id, { reply: true })}
                onDismiss={dismissCard}
                onOpenMessage={openPopupMessage}
                onMore={() => { dispatchPopups({ type: 'dismissAll' }); setOpen(true); }}
            />
        </div>
    );
}

export default NotificationCenterBell;
